// Synthetic destination delivery + impression data for preview mode.
//
// This is the ONLY place the "what the numbers look like" logic lives. Rows are
// generated on demand for whatever destination IDs a query asks for, seeded
// deterministically off the destination ID string (like seed_local_metrics.sh
// hashing md5(destination_id)). That means it works for ANY destination — the
// synthetic preview set on the overview, or a real destination ID on a detail
// page — without a pre-baked, fixed-ID dataset that only matches one env.
//
// Rows are emitted at the grain the production tables hold (one row per hour /
// destination / event def / outcome), so the handlers reconcile the overview
// and detail views by aggregating one shared shape — exactly as the real
// queries do against destination_outcome_metrics_kv / impression_metrics.
//
// Generation is intentionally simple for a first pass; iterate on fidelity here
// without touching the aggregation handlers.

export type DestinationOutcome = "delivered" | "failed" | "dropped" | "delayed";

export interface DestOutcomeRow {
  time: number; // hour bucket, ms since epoch
  destinationID: string;
  destinationType: string;
  eventDefID: string;
  outcome: DestinationOutcome;
  count: number;
  avgLatencyMS: number;
  maxLatencyMS: number;
}

export interface ImpressionRow {
  time: number; // hour bucket, ms since epoch
  destinationID: string;
  destinationType: string;
  count: number;
}

export interface TargetDestination {
  id: string;
  type: string;
  hasDeliveryMetrics: boolean;
  hasImpressions: boolean;
}

const HOUR_MS = 60 * 60 * 1000;

// Deterministic string -> [0,1). FNV-1a hash with a final mix; stable across
// runs so the same (destination, event def, hour) always yields the same value
// and queries on the same page reconcile.
function hashUnit(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h ^= h >>> 13;
  h = Math.imul(h, 0x5bd1e995);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

// Builds through the US workday and falls off overnight: peak ~17:00 UTC,
// trough ~05:00 UTC. Keeps charts from reading as a flat line.
function diurnal(hourOfDay: number): number {
  return 0.55 + 0.45 * Math.sin(((hourOfDay - 11) / 24) * 2 * Math.PI);
}

export function generateOutcomeRows(args: {
  destinations: TargetDestination[];
  eventDefIDs: string[];
  windowStart: number;
  windowEnd: number;
  // Varies the data between the current and previous comparison windows.
  salt?: string;
}): DestOutcomeRow[] {
  const { destinations, eventDefIDs, windowStart, windowEnd, salt = "" } = args;
  const rows: DestOutcomeRow[] = [];
  const firstHour = Math.ceil(windowStart / HOUR_MS) * HOUR_MS;

  for (const dest of destinations) {
    if (!dest.hasDeliveryMetrics) continue;
    const failRate = 0.01 + hashUnit(`${dest.id}:${salt}:fail`) * 0.04;
    const dropRate = 0.005 + hashUnit(`${dest.id}:${salt}:drop`) * 0.02;
    const latencyBase = 50 + hashUnit(`${dest.id}:lat`) * 500;

    for (const defID of eventDefIDs) {
      const pairBase = 5 + hashUnit(`${dest.id}:${defID}`) * 40;

      for (let t = firstHour; t < windowEnd; t += HOUR_MS) {
        const noise = hashUnit(`${dest.id}:${defID}:${salt}:${t}`);
        const shape = diurnal(new Date(t).getUTCHours());
        const vol = pairBase * shape * (0.8 + noise * 0.4);
        const delivered = Math.round(vol);
        const failed = Math.round(vol * failRate * (0.5 + noise));
        const dropped = Math.round(vol * dropRate * (0.5 + noise));
        const avgLatencyMS = Math.round(latencyBase * (0.7 + noise * 0.6));
        const maxLatencyMS = Math.round(latencyBase * (1.5 + noise * 2));

        for (const [outcome, count] of [
          ["delivered", delivered],
          ["failed", failed],
          ["dropped", dropped],
        ] as const) {
          if (count <= 0) continue;
          rows.push({
            time: t,
            destinationID: dest.id,
            destinationType: dest.type,
            eventDefID: defID,
            outcome,
            count,
            avgLatencyMS,
            maxLatencyMS,
          });
        }
      }
    }
  }

  return rows;
}

export function generateImpressionRows(args: {
  destinations: TargetDestination[];
  windowStart: number;
  windowEnd: number;
  salt?: string;
}): ImpressionRow[] {
  const { destinations, windowStart, windowEnd, salt = "" } = args;
  const rows: ImpressionRow[] = [];
  const firstHour = Math.ceil(windowStart / HOUR_MS) * HOUR_MS;

  for (const dest of destinations) {
    if (!dest.hasImpressions) continue;
    const base = 20 + hashUnit(`${dest.id}:imp`) * 120;

    for (let t = firstHour; t < windowEnd; t += HOUR_MS) {
      const noise = hashUnit(`${dest.id}:imp:${salt}:${t}`);
      const shape = diurnal(new Date(t).getUTCHours());
      const count = Math.round(base * shape * (0.8 + noise * 0.4));
      if (count <= 0) continue;
      rows.push({
        time: t,
        destinationID: dest.id,
        destinationType: dest.type,
        count,
      });
    }
  }

  return rows;
}
